/**
 * InvestorStatement — one investor's capital account, beside the table
 * ===================================================================
 * Usability review of 23 October 2026, item 19: *"Hovering or clicking an
 * investor should show their capital account: contributions, withdrawals,
 * P&L and ending balance."*
 *
 * CLAUDE.md §0.46: every investor is a lender, so the capital account is
 * their loan notes, and the figures are the investor report's — the same
 * ones the PDF statement prints, read as JSON so they can be checked. Each
 * line says where it comes from, and a line the book cannot state reads n/a.
 *
 * It opens on a click rather than on hover: each statement is a full read of
 * the investor's notes, a few seconds of server work, and a pointer crossing
 * the table would start one per row.
 */
import React, { useEffect, useRef } from "react";
import useCachedGet from "../../components/useCachedGet";
import { FreshnessBadge, InfoTip } from "../../components/PageTools";
import { day, money, pct } from "../../components/format";
import { capitalAccount } from "./navLogic";

const whole = (v) => money(v, { whole: true });
const bracketed = (v) => (v === null || v === undefined ? whole(v) : whole(-v));

function Line({ label, source, value, strong, children }) {
  return (
    <div className={`nvp-stmt-line${strong ? " nvp-stmt-strong" : ""}`}>
      <div>
        <div className="nvp-stmt-label">{label}{children}</div>
        {source && <div className="nvp-stmt-source">{source}</div>}
      </div>
      <div className="nvp-stmt-value">{value}</div>
    </div>
  );
}

export default function InvestorStatement({ investor, asOfDate, onClose }) {
  const panel = useRef(null);
  const { data, savedAt, loading, refreshing, error, reload } = useCachedGet(
    investor ? "/reports/investor-letter/" : null,
    { params: investor ? { lp_id: investor.lp_id, date: asOfDate, format: "json" } : undefined },
  );

  useEffect(() => {
    if (!investor) return undefined;
    panel.current?.focus();
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [investor, onClose]);

  if (!investor) return null;
  const account = capitalAccount(data?.kpis);

  return (
    <>
      <div className="nvp-scrim no-print" onClick={onClose} aria-hidden="true" />
      <aside
        className="nvp-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nvp-stmt-h"
        tabIndex={-1}
        ref={panel}
      >
        <div className="nvp-drawer-head">
          <div>
            <h2 id="nvp-stmt-h">{investor.name}</h2>
            <p>Capital account as of {day(data?.as_of || asOfDate)}</p>
          </div>
          <button type="button" className="nvp-close" onClick={onClose} aria-label="Close statement">×</button>
        </div>
        <div className="nvp-drawer-fresh">
          <FreshnessBadge savedAt={savedAt} refreshing={refreshing} error={error} onRefresh={reload} />
        </div>
        <p className="nvp-stmt-intro">
          Every investor lends to Helena Partners Inc under a loan note for a named deal, and is repaid out of that
          deal’s proceeds. This account is read from their notes.
        </p>

        {loading && !data ? (
          <p className="nvp-empty">Reading their notes…</p>
        ) : error && !data ? (
          <p className="nvp-empty">Could not load this statement. {error}</p>
        ) : !account ? (
          <p className="nvp-empty">No statement is available for this investor.</p>
        ) : (
          <div className="nvp-stmt">
            <Line label="Contributions" source="Cash they lent, from their loan notes" value={whole(account.contributions)} />
            <Line
              label="Withdrawals"
              source="Distributions declared to them out of their notes"
              value={bracketed(account.withdrawals)}
            />
            <Line label="Profit realized" source="Profit on their notes from deals that have paid out" value={whole(account.profit)} />
            <div className="nvp-stmt-detail">
              <span>paid or re-invested</span><span>{whole(account.profitPaid)}</span>
              <span>realized, not yet paid</span><span>{whole(account.profitToPay)}</span>
            </div>
            <Line
              label="Difference"
              source="Moves on the notes not shown above"
              value={whole(account.difference)}
            >
              {" "}
              <InfoTip align="right" label="What the difference is">
                <p>
                  The ending balance less contributions, less withdrawals, plus profit. The notes also move when money
                  is recycled from one deal into another, when a loss is written off against them, and while bookings
                  are still to be made; none of those is a line here, so the four lines do not add to the balance on
                  their own.
                </p>
                {account.recycled !== null && (
                  <p>Proceeds recycled between their deals to date: <b>{whole(account.recycled)}</b>.</p>
                )}
              </InfoTip>
            </Line>
            <Line
              strong
              label="Ending balance"
              source={account.owedBack
                ? `Notes outstanding ${whole(account.notes)}, less ${whole(account.owedBack)} they owe back from an over-distribution`
                : "What their notes still stand at"}
              value={whole(account.ending)}
            />

            <div className="nvp-stmt-section">Not in the notes</div>
            <Line
              label="Unrealised value of open positions"
              source="Their share of positions still held, as the investor report values them"
              value={account.unrealised === null ? "n/a" : whole(account.unrealised)}
            >
              {account.unrealised === null && (
                <>
                  {" "}
                  <InfoTip align="right" label="Why this is n/a">
                    <p>
                      At least one of their deals could not be valued in dollars on this date, so a total would be a
                      part presented as the whole.
                    </p>
                  </InfoTip>
                </>
              )}
            </Line>
            <Line
              label="Share of NAV on this page"
              source={`${pct(investor.ownership_pct, 2)} ownership, as recorded on their deals`}
              value={whole(investor.nav_share)}
            />
          </div>
        )}
      </aside>
    </>
  );
}
